import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { createTheme, ThemeOptions, Theme } from '@material-ui/core';
import { ThemeNames, CustomTheme, CustomThemeOptions } from './themeTypes';
import themes from './themes';

const nonMuiThemeOptions = {
  custom: {
    app: {
      padding: '8px',
    },
    icon: {
      error: '#b4b4b4',
      primary: '#4e9d2d',
    },
  },
} as CustomTheme;

export const overrideBaseTheme = (...overrideThemes: CustomThemeOptions[]): Theme => createTheme(
  {
    typography: {
      fontFamily: '"Open Sans", sans-serif',
      h1: {
        fontSize: 26,
        fontWeight: 700,
        margin: 0,
        marginBottom: 30,
        textAlign: 'center',
      },
      subtitle1: {
        fontSize: 16,
        color: '#727272',
        marginBottom: 30,
        lineHeight: '22px',
        textAlign: 'center',
      },
      body2: {
        marginTop: 20,
      },
    },
    palette: {
      primary: {
        main: '#005de8',
      },
      secondary: {
        main: '#fff',
      },
      error: {
        main: '#d6083b',
      },
    },
    props: {
      MuiAlert: {
        iconMapping: {
          error: <FontAwesomeIcon icon={faTimes} mask={faCircle} transform='shrink-5' />,
        },
      },
      MuiButton: {
        type: 'submit',
        fullWidth: true,
        disableRipple: true,
        color: 'primary',
        variant: 'contained',
      },
      MuiTextField: {
        variant: 'outlined',
        size: 'small',
        fullWidth: true,
      },
      MuiLink: {
        underline: 'none',
      },
      MuiGrid: {
        justifyContent: 'center',
      },
      MuiTypography: {
        variant: 'subtitle1',
        variantMapping: {
          subtitle1: 'p',
        },
      },
    },
    overrides: {
      MuiAlert: {
        root: {
          marginTop: 0,
          marginBottom: 30,
          fontSize: 11,
          borderLeftStyle: 'solid',
          borderLeftWidth: 3,
        },
        standardError: {
          color: '#222121',
          backgroundColor: 'rgba(214, 8, 59, .1)',
          borderLeftColor: '#d6083b',
        },
        message: {
          padding: '11px 0',
          alignSelf: 'center',
        },
      },
      MuiButton: {
        contained: {
          height: 46,
          fontSize: 11,
          fontWeight: 700,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          '&:active': {
            boxShadow: 'none',
          },
          '&:focus': {
            '&::before': {
              top: -4,
              right: -4,
              bottom: -4,
              left: -4,
              border: '1px solid #58de79',
              boxShadow: '0 0 4px 0 rgba(88, 222, 121, 0.4)',
              background: 'rgba(88, 222, 121, 0.1)',
              display: 'block',
              position: 'absolute',
              zIndex: -1,
              borderRadius: 4,
              content: '""',
            },
          },
          '&$focusVisible': {
            boxShadow: 'none',
          },
        },
      },
      MuiCard: {
        root: {
          marginBottom: 15,
          borderRadius: 4,
          boxShadow: 'none',
          backgroundColor: '#f5f8fa',
          padding: 20,
          fontSize: 14,
        },
      },
      MuiTextField: {
        root: {
          marginBottom: 15,
        },
      },
      MuiInput: {
        underline: {
          '&:before': {
            borderBottom: 'solid #e0e0e0 2px',
          },
          '&:hover:not($disabled):before': {
            borderBottom: 'solid #e0e0e0 2px',
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          backgroundColor: '#f2f7fe',
          height: 42,
          fontSize: 14,
          '&$focused $notchedOutline': {
            borderColor: '#e0e0e0',
            borderWidth: 1,
          },
          '&:hover $notchedOutline': {
            borderColor: '#e0e0e0',
          },
        },
        notchedOutline: {
          borderColor: 'transparent',
          borderWidth: 1,
        },
      },
      MuiLink: {
        root: {
          color: '#005de8',
          '&:hover': {
            color: '#0049b5',
          },
        },
        button: {
          verticalAlign: 'baseline',
        },
      },
      MuiSkeleton: {
        root: {
          display: 'inline',
        },
      },
      MuiTypography: {
        srOnly: {
          fontSize: 16,
          marginBottom: 30,
          lineHeight: '22px',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          width: 0,
          height: 'auto',
          position: 'relative',
        },
      },
      MuiInputLabel: {
        root: {
          marginBottom: 8,
          lineHeight: '20px',
          fontSize: 14,
          fontWeight: 600,
          color: '#222121',
        },
        formControl: {
          color: '#727272',
          fontWeight: 400,
        },
      },
    },
  } as ThemeOptions,
  nonMuiThemeOptions,
  ...overrideThemes,
);

export const makeTheme = (themeName?: ThemeNames): Theme => {
  const overrideTheme = themeName ? themes[themeName] : {};
  return overrideBaseTheme(overrideTheme);
};
